import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search, Info, RefreshCw, Fingerprint } from "lucide-react";
import axios from "axios";
import Apiurl from "../../environment/Apiurl";
import { useNavigate } from "react-router-dom";

function Attendance() {
  const navigate = useNavigate();

  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingBiometric, setSyncingBiometric] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("today");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [activePage, setActivePage] = useState(1);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);

  // Form state for adding
  const [addData, setAddData] = useState({
    select_type: "",
    doctor_id: "",
    staff_id: "",
    first_name: "",
    date: "",
    login: "",
    logout: "",
    lunch_in: "",
    lunch_out: "",
  });

  // Form state for editing
  const [updateData, setUpdateData] = useState({
    select_type: "",
    doctor_id: "",
    staff_id: "",
    first_name: "",
    date: "",
    login: "",
    logout: "",
    lunch_in: "",
    lunch_out: "",
  });

  // Updated time formatting functions
  const formatTimeForDisplay = (time) => {
    if (!time) return "-";
    if (time.includes("AM") || time.includes("PM")) return time;

    try {
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes.padStart(2, "0")} ${ampm}`;
    } catch {
      console.warn(`Invalid time format: ${time}`);
      return time; // Return original if we can't format it
    }
  };

  const formatTimeForInput = (time) => {
    if (!time) return "";
    if (!time.includes("AM") && !time.includes("PM")) return time;

    try {
      const [timePart, ampm] = time.split(" ");
      let [hours, minutes] = timePart.split(":");
      hours = parseInt(hours, 10);

      if (ampm === "PM" && hours < 12) hours += 12;
      if (ampm === "AM" && hours === 12) hours = 0;

      return `${hours.toString().padStart(2, "0")}:${minutes}`;
    } catch {
      console.warn(`Invalid time format for input: ${time}`);
      return time; // Return original if we can't format it
    }
  };

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Staff and doctor data
  const [staffDataGet, setStaffDataGet] = useState([]);
  const [doctorDataGet, setDoctorDataGet] = useState([]);

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);
      if (userRoleId === "unknown") {
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setLoadingPrivileges(false);
        return;
      }
      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);
      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          privileges[page.page] = {
            access: page.access,
            ...page.priviallages,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
      }
    } catch (error) {
      console.error("Error fetching user privileges:", error.message);
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Attendance page
  const hasPermission = (permission) => {
    if (!userPrivileges || !userPrivileges["Attendance"]) {
      console.log("No user privileges or Attendance page data found");
      return false;
    }
    const privileges = userPrivileges["Attendance"];
    console.log(
      `Checking permission '${permission}' for Attendance:`,
      privileges
    );
    if (permission === "access") {
      return privileges.access === true;
    }
    if (permission in privileges && privileges[permission] === false) {
      console.log(`Permission '${permission}' explicitly denied`);
      return false;
    }
    const hasPerm =
      privileges.access && (privileges.all || privileges[permission]);
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  // Fetch attendance data
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Attendance page");
      setError("You do not have permission to view attendance records.");
      return;
    }
    setLoading(true);
    try {
      const userType = localStorage.getItem("user_type") || "unknown";
      const username = localStorage.getItem("username") || "";
      let attendanceData = [];

      if (userType === "Doctor") {
        if (!username) {
          throw new Error("Doctor username not found in localStorage");
        }
        console.log(`Fetching attendance for doctor_id: ${username}`);
        const response = await axios.get(
          `${Apiurl}/attendance_by_doctor_id/${username}`
        );
        console.log(
          `Fetched attendance data for doctor_id: ${username}`,
          response.data
        );
        // Normalize and reverse response
        attendanceData = Array.isArray(response.data)
          ? response.data
          : response.data
          ? [response.data]
          : [];

        if (attendanceData.length === 0) {
          console.warn(
            `No attendance records found for doctor_id: ${username}`
          );
        }
      } else {
        console.log("Fetching all attendance records");
        const response = await axios.get(`${Apiurl}/attendance`);
        console.log("Fetched all attendance data:", response.data);
        attendanceData = Array.isArray(response.data)
          ? response.data
          : response.data
          ? [response.data]
          : [];

        if (attendanceData.length === 0) {
          console.warn("No attendance records found");
        }
      }

      // Reverse data to show most recent first
      attendanceData.reverse();

      setResponseData(attendanceData);
      setError(null);
    } catch (error) {
      console.error("Error fetching attendance:", error.message);
      setError("Failed to fetch attendance records. Please try again.");
      setResponseData([]);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Pull the latest punches from the ZKTeco machine and refresh the table.
  const syncBiometricAttendance = async () => {
    if (!hasPermission("read")) {
      setToast({ type: "error", message: "You do not have permission to sync attendance." });
      return;
    }

    setSyncingBiometric(true);
    try {
      const response = await axios.post(`${Apiurl}/zk-attendance/sync`);
      const result = response.data || {};
      const unknown = Array.isArray(result.unknownDeviceIds)
        ? result.unknownDeviceIds.length
        : 0;

      setToast({
        type: "success",
        message: `Biometric sync completed. ${result.punchesApplied || 0} punches processed and ${result.recordsCreated || 0} new attendance record(s) created.${unknown ? ` ${unknown} device ID(s) are not mapped to staff.` : ""}`,
      });
      await fetchData();
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Unable to connect to the ZKTeco biometric machine.";
      setToast({ type: "error", message });
    } finally {
      setSyncingBiometric(false);
    }
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Fetch staff data
  const fetchStaffData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for staff data");
      setError("You do not have permission to view staff data.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/staff`);
      console.log("Staff data fetched:", response.data);
      setStaffDataGet(response.data || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching staff data:", error.message);
      setError("Failed to fetch staff data. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Fetch doctor data
  const fetchDoctorData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for doctor data");
      setError("You do not have permission to view doctor data.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${Apiurl}/doctor`);
      console.log("Doctor data fetched:", response.data);
      setDoctorDataGet(response.data || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching doctor data:", error.message);
      setError("Failed to fetch doctor data. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Format time for display (e.g., convert to 12h format with AM/PM)

  // Validate form data
  const validateForm = (data) => {
    if (!data.select_type) return "Select type is required.";
    if (data.select_type === "Staff" && !data.staff_id)
      return "Staff ID is required.";
    if (data.select_type === "Doctor" && !data.doctor_id)
      return "Doctor ID is required.";
    if (!data.first_name) return "Name is required.";
    if (!data.date) return "Date is required.";
    if (!data.login) return "Log In time is required.";
    return null;
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Attendance page");
      setError("You do not have permission to add attendance records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(addData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const formdata = {
        select_type: addData.select_type,
        doctor_id: addData.doctor_id,
        staff_id: addData.staff_id,
        first_name: addData.first_name,
        date: addData.date,
        login: formatTimeForInput(addData.login),
        logout: formatTimeForInput(addData.logout),
        lunch_in: formatTimeForInput(addData.lunch_in),
        lunch_out: formatTimeForInput(addData.lunch_out),
      };
      console.log("Adding attendance with data:", formdata);
      await axios.post(`${Apiurl}/attendance`, formdata);
      setAddData({
        select_type: "",
        doctor_id: "",
        staff_id: "",
        first_name: "",
        date: "",
        login: "",
        logout: "",
        lunch_in: "",
        lunch_out: "",
      });
      fetchData();
      closeAddModal();
    } catch (error) {
      console.error("Error adding attendance:", error.message);
      setError("Failed to add attendance. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Attendance page");
      setError("You do not have permission to edit attendance records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(updateData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const formdata = {
        select_type: updateData.select_type,
        doctor_id: updateData.doctor_id,
        staff_id: updateData.staff_id,
        first_name: updateData.first_name,
        date: updateData.date,
        login: formatTimeForInput(updateData.login),
        logout: formatTimeForInput(updateData.logout),
        lunch_in: formatTimeForInput(updateData.lunch_in),
        lunch_out: formatTimeForInput(updateData.lunch_out),
      };
      console.log("Updating attendance with ID:", editingId, "Data:", formdata);
      await axios.put(`${Apiurl}/attendance/${editingId}`, formdata);
      fetchData();
      closeEditModal();
    } catch (error) {
      console.error("Error updating attendance:", error.message);
      setError("Failed to update attendance. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle attendance view navigation
  const handleAttendanceView = (id, item) => {
    if (hasPermission("view")) {
      console.log("Navigating to attendance calendar");
      const staffId =
        item.select_type === "Staff" ? item.staff_id : item.doctor_id;
      navigate(`/hr-management/attendance/attendance-calendar/${staffId}`);
    } else {
      console.warn("View permission denied for Attendance page");
      setError("You do not have permission to view the attendance calendar.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Edit attendance
  const handleEdit = (id, item) => {
    if (hasPermission("update")) {
      if (!id) {
        console.warn("Invalid attendance ID:", id);
        setError("Invalid attendance ID.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      console.log("Opening Edit Attendance modal for ID:", id, "Item:", item);
      setEditingId(id);
      setUpdateData({
        select_type: item.select_type || "",
        doctor_id: item.doctor_id || "",
        staff_id: item.staff_id || "",
        first_name: item.first_name || "",
        date: item.date || "",
        login: item.login ? formatTimeForDisplay(item.login) : "",
        logout: item.logout ? formatTimeForDisplay(item.logout) : "",
        lunch_in: item.lunch_in ? formatTimeForDisplay(item.lunch_in) : "",
        lunch_out: item.lunch_out ? formatTimeForDisplay(item.lunch_out) : "",
      });
      setShowEditModal(true);
    } else {
      console.warn("Update permission denied for Attendance page");
      setError("You do not have permission to edit attendance records.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (hasPermission("delete")) {
      if (!id) {
        console.warn("Invalid attendance ID:", id);
        setError("Invalid attendance ID.");
        setTimeout(() => setError(null), 3000);
        return;
      }
      console.log("Opening Delete Attendance modal for ID:", id);
      setDeleteId(id);
      setShowDeleteModal(true);
    } else {
      console.warn("Delete permission denied for Attendance page");
      setError("You do not have permission to delete attendance records.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Attendance modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Handle delete attendance
  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Attendance page");
      setError("You do not have permission to delete attendance records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid attendance ID:", deleteId);
      setError("Invalid attendance ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting attendance with ID:", deleteId);
      await axios.delete(`${Apiurl}/attendance/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting attendance:", error.message);
      setError("Failed to delete attendance. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Open add modal
  const openAddModal = () => {
    if (hasPermission("create")) {
      console.log("Opening Add Attendance modal");
      setShowAddModal(true);
    } else {
      console.warn("Create permission denied for Attendance page");
      setError("You do not have permission to add attendance records.");
      setTimeout(() => setError(null), 3000);
    }
  };

  // Close add modal
  const closeAddModal = () => {
    console.log("Closing Add Attendance modal");
    setShowAddModal(false);
    setAddData({
      select_type: "",
      doctor_id: "",
      staff_id: "",
      first_name: "",
      date: "",
      login: "",
      logout: "",
      lunch_in: "",
      lunch_out: "",
    });
    setError(null);
  };

  // Close edit modal
  const closeEditModal = () => {
    console.log("Closing Edit Attendance modal");
    setShowEditModal(false);
    setEditingId(null);
    setUpdateData({
      select_type: "",
      doctor_id: "",
      staff_id: "",
      first_name: "",
      date: "",
      login: "",
      logout: "",
      lunch_in: "",
      lunch_out: "",
    });
    setError(null);
  };

  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (hasPermission("access")) {
      console.log("Access granted for Attendance page");
      if (hasPermission("read")) {
        console.log(
          "Fetching attendance, staff, and doctor data due to read permission"
        );
        fetchData();
        fetchStaffData();
        fetchDoctorData();
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Attendance page");
      setError("You do not have permission to access this page.");
    }
  }, [userPrivileges, loadingPrivileges]);

  // Real-time ZKTeco updates: the backend pushes an SSE event as soon as a
  // new biometric punch is processed. No page refresh or 5/15-second table
  // polling is needed. The backend's short device polling interval is only
  // used to detect the machine punch; the browser itself is event-driven.
  useEffect(() => {
    if (loadingPrivileges || !hasPermission("access") || !hasPermission("read")) {
      return undefined;
    }

    const apiBase = String(Apiurl).replace(/\/api\/?$/, "");
    const stream = new EventSource(`${apiBase}/api/zk-attendance/stream`);

    const handleAttendanceUpdate = (event) => {
      // IMPORTANT: Do not call fetchData() here. That causes the whole table
      // to reload every few seconds. The backend sends only the changed
      // attendance record(s), so update React state in place only when an
      // actual biometric punch is detected.
      try {
        const payload = JSON.parse(event?.data || "{}");
        const records = Array.isArray(payload.records) ? payload.records : [];
        if (!records.length) return;

        setResponseData((current) => {
          const next = [...current];
          records.forEach((record) => {
            const id = String(record?._id || record?.id || "");
            const staffId = String(record?.staff_id || "");
            const date = String(record?.date || "");
            const index = next.findIndex((item) => {
              const itemId = String(item?._id || item?.id || "");
              return id ? itemId === id :
                String(item?.staff_id || "") === staffId && String(item?.date || "") === date;
            });
            if (index >= 0) next[index] = { ...next[index], ...record };
            else next.unshift(record);
          });
          return next;
        });
      } catch (error) {
        console.warn("Invalid ZKTeco live attendance event:", error);
      }
    };

    stream.addEventListener("attendance_updated", handleAttendanceUpdate);
    stream.onerror = () => {
      // EventSource automatically reconnects. Do not refresh the page or
      // start a polling fallback here.
      console.warn("ZKTeco live attendance stream disconnected; retrying automatically.");
    };

    return () => {
      stream.removeEventListener("attendance_updated", handleAttendanceUpdate);
      stream.close();
    };
  }, [userPrivileges, loadingPrivileges]);

  // Filter data with useMemo

  const toDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return "";
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getLocalDateKey = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const matchesDateFilter = (value) => {
    if (dateFilter === "all") return true;

    const itemDate = toDateKey(value);
    if (!itemDate) return false;

    const today = new Date();
    const todayKey = getLocalDateKey(today);

    if (dateFilter === "today") return itemDate === todayKey;

    if (dateFilter === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return itemDate === getLocalDateKey(yesterday);
    }

    if (dateFilter === "last_week") {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return itemDate >= getLocalDateKey(start) && itemDate <= todayKey;
    }

    if (dateFilter === "last_month") {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      return itemDate >= getLocalDateKey(start) && itemDate <= todayKey;
    }

    if (dateFilter === "custom") {
      if (!customFromDate && !customToDate) return true;
      if (customFromDate && itemDate < customFromDate) return false;
      if (customToDate && itemDate > customToDate) return false;
      return true;
    }

    return true;
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setCurrentPage(1);
    setActivePage(1);
  };

  const filteredData = useMemo(() => {
    console.log("Filtering data with search term:", searchTerm);
    const filtered = responseData.filter((item) => {
      const matchesSearch =
        (item.date?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.staff_id?.toString().includes(searchTerm) ||
          item.doctor_id?.toString().includes(searchTerm)) ??
        false;

      return matchesSearch && matchesDateFilter(item.date);
    });
    console.log("Filtered data:", filtered);
    return filtered;
  }, [
    responseData,
    searchTerm,
    dateFilter,
    customFromDate,
    customToDate,
  ]);

  // Update items per page when showEntries changes
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
    setCurrentPage(1);
    setActivePage(1);
  }, [showEntries, filteredData]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log("Current items for display:", currentItems);

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          activePage - 1,
          activePage,
          activePage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== activePage) {
      console.log("Navigating to page:", page);
      setActivePage(page);
      setCurrentPage(page);
    }
  };

  if (loadingPrivileges) {
    return <div className="text-center py-5">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Attendance page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-3 position-relative">
        {toast && (
          <div
            className={`position-fixed top-0 end-0 mt-3 me-3 alert ${
              toast.type === "success" ? "alert-success" : "alert-danger"
            } shadow-sm d-flex align-items-start gap-2`}
            style={{ zIndex: 2000, maxWidth: "430px" }}
            role="alert"
          >
            <Fingerprint size={18} className="mt-1 flex-shrink-0" />
            <div className="flex-grow-1">{toast.message}</div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setToast(null)}
            />
          </div>
        )}

        <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-sm text-white d-flex align-items-center gap-2"
            style={{ backgroundColor: "#01AEBE" }}
            onClick={syncBiometricAttendance}
            disabled={syncingBiometric}
            title="Read the latest attendance punches from the ZKTeco machine"
          >
            <RefreshCw size={15} className={syncingBiometric ? "spin" : ""} />
            {syncingBiometric ? "Syncing..." : "Sync Biometric"}
          </button>

          {hasPermission("create") && (
            <button
              className="btn btn-sm text-white"
              style={{ backgroundColor: "#01AEBE" }}
              onClick={openAddModal}
            >
              + Add Attendance
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mt-2">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        {hasPermission("read") && (
          <div className="card card-body mt-2">
            <div className="bg-white rounded shadow-sm">

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 crm-filter-tabs">
              {[
                ["today", "Today"],
                ["yesterday", "Yesterday"],
                ["last_week", "Last Week"],
                ["last_month", "Last Month"],
                ["all", "All"],
                ["custom", "Custom Date"],
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn-sm ${
                    dateFilter === value ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => handleDateFilterChange(value)}
                >
                  {label}
                </button>
              ))}

              {dateFilter === "custom" && (
                <div className="d-flex flex-wrap align-items-center gap-2 ms-md-2">
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">From</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customFromDate}
                      onChange={(e) => {
                        setCustomFromDate(e.target.value);
                        handleDateFilterChange("custom");
                      }}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">To</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customToDate}
                      onChange={(e) => {
                        setCustomToDate(e.target.value);
                        handleDateFilterChange("custom");
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
              <div className="row mb-3 crm-table-toolbar">
                <div className="col-md-2">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-table"></i>
                    </span>
                    <select
                      className="form-select"
                      value={showEntries}
                      onChange={(e) => {
                        console.log(
                          "Changing items per page to:",
                          e.target.value
                        );
                        setShowEntries(e.target.value);
                      }}
                    >
                      <option value="All">Show All</option>
                      {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                        <option key={n} value={n}>
                          Show {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-7"></div>
                <div className="col-md-3">
                  <div className="d-flex align-items-center">
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control pe-5"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => {
                          console.log(
                            "Search term changed to:",
                            e.target.value
                          );
                          setSearchTerm(e.target.value);
                        }}
                        style={{ width: "250px", paddingRight: "2.5rem" }}
                      />
                      <Search
                        size={16}
                        className="position-absolute text-muted"
                        style={{
                          right: "10px",
                          top: "35%",
                          transform: "translateY(-50%)",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {loading && <div className="text-center py-3">Loading...</div>}

              <div className="table-responsive">
                <table className="table responsive-table">
                  <thead className="table-light">
                    <tr style={{ backgroundColor: "#01AEBE", color: "white" }}>
                      <th>S.No</th>
                      <th>Staff Id</th>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Log In</th>
                      <th>Lunch In</th>
                      <th>Lunch Out</th>
                      <th>Log Out</th>
                      <th>Shift</th>
                      <th>Status</th>
                      <th>Working Hours</th>
                      {(hasPermission("update") ||
                        hasPermission("delete") ||
                        hasPermission("view")) && <th>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((item, index) => (
                        <tr key={item._id || index} className="align-middle">
                          <td>{indexOfFirstItem + index + 1}</td>
                          <td>{item.doctor_id || item.staff_id || "N/A"}</td>
                          <td>{item.first_name || "-"}</td>
                          <td>{item.date || "-"}</td>
                          <td>{formatTimeForDisplay(item.login)}</td>
                          <td>{formatTimeForDisplay(item.lunch_in)}</td>
                          <td>{formatTimeForDisplay(item.lunch_out)}</td>
                          <td>{formatTimeForDisplay(item.logout)}</td>
                        <td>{item.shift_name || "-"}{item.shift_login_time ? ` (${formatTimeForDisplay(item.shift_login_time)})` : ""}</td>
                        <td><span className={`badge ${item.attendance_status === "Late" ? "bg-danger" : item.attendance_status === "On Time" ? "bg-success" : "bg-secondary"}`}>{item.attendance_status || "-"}</span></td>
                        <td>{item.working_hours || "-"}</td>
                          {(hasPermission("update") ||
                            hasPermission("delete") ||
                            hasPermission("view")) && (
                            <td>
                              {hasPermission("delete") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() => openDeleteModal(item._id)}
                                >
                                  <Trash2 size={16} className="text-danger" />
                                </button>
                              )}
                              {hasPermission("update") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() => handleEdit(item._id, item)}
                                >
                                  <Edit size={16} className="text-primary" />
                                </button>
                              )}
                              {hasPermission("view") && (
                                <button
                                  className="btn btn-sm p-1 border-0"
                                  style={{ backgroundColor: "transparent" }}
                                  onClick={() =>
                                    handleAttendanceView(item.id, item)
                                  }
                                >
                                  <Info size={16} className="text-primary" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={
                            hasPermission("update") ||
                            hasPermission("delete") ||
                            hasPermission("view")
                              ? 9
                              : 8
                          }
                          className="text-center py-3"
                        >
                          {loading
                            ? "Loading..."
                            : "No attendance records found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredData.length > 0 && (
                <div className="container">
                  <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      onClick={() => {
                        if (activePage > 1) {
                          setActivePage(activePage - 1);
                          setCurrentPage(activePage - 1);
                        }
                      }}
                      disabled={activePage === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                      <span className="d-none d-sm-inline">Previous</span>
                    </button>

                    <div
                      className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                      style={{ maxWidth: "100%" }}
                    >
                      {pages.map((page, index) => (
                        <button
                          key={index}
                          className={`btn btn-sm px-3 flex-shrink-0 ${
                            page === activePage
                              ? "text-white"
                              : "btn-outline-secondary"
                          }`}
                          style={
                            page === activePage
                              ? { backgroundColor: "#20c997", border: "none" }
                              : {}
                          }
                          onClick={() => handlePageClick(page)}
                          disabled={page === "..."}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      onClick={() => {
                        if (activePage < totalPages) {
                          setActivePage(activePage + 1);
                          setCurrentPage(activePage + 1);
                        }
                      }}
                      disabled={activePage === totalPages}
                    >
                      <span className="d-none d-sm-inline">Next</span>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Attendance Modal */}
      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addAttendanceModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6
                  className="modal-title text-dark"
                  id="addAttendanceModalLabel"
                >
                  Add Attendance
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="select_type"
                          className="form-label fw-bold"
                        >
                          Select Type
                        </label>
                        <select
                          className="form-select"
                          id="select_type"
                          name="select_type"
                          value={addData.select_type}
                          onChange={(e) => {
                            console.log("Select type changed:", e.target.value);
                            setAddData({
                              ...addData,
                              select_type: e.target.value,
                              staff_id: "",
                              doctor_id: "",
                              first_name: "",
                            });
                          }}
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="Staff">Staff</option>
                          <option value="Doctor">Doctor</option>
                        </select>
                      </div>
                    </div>
                    {addData.select_type === "Staff" && (
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label
                            htmlFor="staff_id"
                            className="form-label fw-bold"
                          >
                            Staff ID
                          </label>
                          <select
                            className="form-control"
                            id="staff_id"
                            name="staff_id"
                            value={addData.staff_id}
                            onChange={(e) => {
                              const selectedStaff = staffDataGet.find(
                                (staff) => staff.staff_id === e.target.value
                              );
                              console.log(
                                "Selected staff ID:",
                                e.target.value,
                                selectedStaff
                              );
                              setAddData({
                                ...addData,
                                staff_id: e.target.value,
                                doctor_id: "",
                                first_name: selectedStaff
                                  ? `${selectedStaff.first_name} ${
                                      selectedStaff.last_name || ""
                                    }`
                                  : "",
                              });
                            }}
                            required
                          >
                            <option value="">Select Staff</option>
                            {staffDataGet.map((item) => (
                              <option key={item._id} value={item.staff_id}>
                                {item.staff_id} - {item.first_name}{" "}
                                {item.last_name || ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    {addData.select_type === "Doctor" && (
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label
                            htmlFor="doctor_id"
                            className="form-label fw-bold"
                          >
                            Doctor ID
                          </label>
                          <select
                            className="form-control"
                            id="doctor_id"
                            name="doctor_id"
                            value={addData.doctor_id}
                            onChange={(e) => {
                              const selectedDoctor = doctorDataGet.find(
                                (doc) => doc.doctor_id === e.target.value
                              );
                              console.log(
                                "Selected doctor ID:",
                                e.target.value,
                                selectedDoctor
                              );
                              setAddData({
                                ...addData,
                                doctor_id: e.target.value,
                                staff_id: "",
                                first_name: selectedDoctor
                                  ? selectedDoctor.doctor_name
                                  : "",
                              });
                            }}
                            required
                          >
                            <option value="">Select Doctor</option>
                            {doctorDataGet.map((item) => (
                              <option key={item._id} value={item.doctor_id}>
                                {item.doctor_id} - {item.doctor_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="first_name"
                          className="form-label fw-bold"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="first_name"
                          name="first_name"
                          placeholder="Enter Name"
                          value={addData.first_name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="date" className="form-label fw-bold">
                          Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="date"
                          name="date"
                          value={addData.date}
                          onChange={(e) => {
                            console.log("Date input changed:", e.target.value);
                            setAddData({
                              ...addData,
                              date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="login" className="form-label fw-bold">
                          Log In
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="login"
                          name="login"
                          value={addData.login}
                          onChange={(e) => {
                            console.log(
                              "Log In input changed:",
                              e.target.value
                            );
                            setAddData({
                              ...addData,
                              login: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="lunch_in"
                          className="form-label fw-bold"
                        >
                          Lunch In
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="lunch_in"
                          name="lunch_in"
                          value={addData.lunch_in}
                          onChange={(e) => {
                            console.log(
                              "Lunch In input changed:",
                              e.target.value
                            );
                            setAddData({
                              ...addData,
                              lunch_in: e.target.value,
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="lunch_out"
                          className="form-label fw-bold"
                        >
                          Lunch Out
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="lunch_out"
                          name="lunch_out"
                          value={addData.lunch_out}
                          onChange={(e) => {
                            console.log(
                              "Lunch Out input changed:",
                              e.target.value
                            );
                            setAddData({
                              ...addData,
                              lunch_out: e.target.value,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="logout" className="form-label fw-bold">
                          Log Out
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="logout"
                          name="logout"
                          value={addData.logout}
                          onChange={(e) => {
                            console.log(
                              "Log Out input changed:",
                              e.target.value
                            );
                            setAddData({
                              ...addData,
                              logout: e.target.value,
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeAddModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Attendance Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editAttendanceModalLabel"
          aria-hidden={!showEditModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6
                  className="modal-title text-dark"
                  id="editAttendanceModalLabel"
                >
                  Edit Attendance
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="select_type"
                          className="form-label fw-bold"
                        >
                          Select Type
                        </label>
                        <select
                          className="form-select"
                          id="select_type"
                          name="select_type"
                          value={updateData.select_type}
                          onChange={(e) => {
                            console.log(
                              "Select type changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              select_type: e.target.value,
                              staff_id: "",
                              doctor_id: "",
                              first_name: "",
                            });
                          }}
                          required
                        >
                          <option value="">Select Type</option>
                          <option value="Staff">Staff</option>
                          <option value="Doctor">Doctor</option>
                        </select>
                      </div>
                    </div>
                    {updateData.select_type === "Staff" && (
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label
                            htmlFor="staff_id"
                            className="form-label fw-bold"
                          >
                            Staff ID
                          </label>
                          <select
                            className="form-control"
                            id="staff_id"
                            name="staff_id"
                            value={updateData.staff_id}
                            onChange={(e) => {
                              const selectedStaff = staffDataGet.find(
                                (staff) => staff.staff_id === e.target.value
                              );
                              console.log(
                                "Selected staff ID for edit:",
                                e.target.value,
                                selectedStaff
                              );
                              setUpdateData({
                                ...updateData,
                                staff_id: e.target.value,
                                doctor_id: "",
                                first_name: selectedStaff
                                  ? `${selectedStaff.first_name} ${
                                      selectedStaff.last_name || ""
                                    }`
                                  : "",
                              });
                            }}
                            required
                          >
                            <option value="">Select Staff</option>
                            {staffDataGet.map((staff) => (
                              <option key={staff._id} value={staff.staff_id}>
                                {staff.staff_id} - {staff.first_name}{" "}
                                {staff.last_name || ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    {updateData.select_type === "Doctor" && (
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label
                            htmlFor="doctor_id"
                            className="form-label fw-bold"
                          >
                            Doctor ID
                          </label>
                          <select
                            className="form-control"
                            id="doctor_id"
                            name="doctor_id"
                            value={updateData.doctor_id}
                            onChange={(e) => {
                              const selectedDoctor = doctorDataGet.find(
                                (doc) => doc.doctor_id === e.target.value
                              );
                              console.log(
                                "Selected doctor ID for edit:",
                                e.target.value,
                                selectedDoctor
                              );
                              setUpdateData({
                                ...updateData,
                                doctor_id: e.target.value,
                                staff_id: "",
                                first_name: selectedDoctor
                                  ? selectedDoctor.doctor_name
                                  : "",
                              });
                            }}
                            required
                          >
                            <option value="">Select Doctor</option>
                            {doctorDataGet.map((item) => (
                              <option key={item._id} value={item.doctor_id}>
                                {item.doctor_id} - {item.doctor_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="first_name"
                          className="form-label fw-bold"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="first_name"
                          name="first_name"
                          placeholder="Enter Name"
                          value={updateData.first_name}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="date" className="form-label fw-bold">
                          Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="date"
                          name="date"
                          value={updateData.date}
                          onChange={(e) => {
                            console.log(
                              "Date input changed for edit:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              date: e.target.value,
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="login" className="form-label fw-bold">
                          Log In
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="login"
                          name="login"
                          value={
                            updateData.login.includes("AM") ||
                            updateData.login.includes("PM")
                              ? formatTimeForInput(updateData.login)
                              : updateData.login
                          }
                          onChange={(e) => {
                            console.log(
                              "Log In input changed:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              login: formatTimeForDisplay(e.target.value),
                            });
                          }}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="lunch_in"
                          className="form-label fw-bold"
                        >
                          Lunch In
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="lunch_in"
                          name="lunch_in"
                          value={
                            updateData.lunch_in.includes("AM") ||
                            updateData.lunch_in.includes("PM")
                              ? formatTimeForInput(updateData.lunch_in)
                              : updateData.lunch_in
                          }
                          onChange={(e) => {
                            console.log(
                              "Lunch In input changed:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              lunch_in: formatTimeForDisplay(e.target.value),
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label
                          htmlFor="lunch_out"
                          className="form-label fw-bold"
                        >
                          Lunch Out
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="lunch_out"
                          name="lunch_out"
                          value={
                            updateData.lunch_out.includes("AM") ||
                            updateData.lunch_out.includes("PM")
                              ? formatTimeForInput(updateData.lunch_out)
                              : updateData.lunch_out
                          }
                          onChange={(e) => {
                            console.log(
                              "Lunch Out input changed:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              lunch_out: formatTimeForDisplay(e.target.value),
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label htmlFor="logout" className="form-label fw-bold">
                          Log Out
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="logout"
                          name="logout"
                          value={
                            updateData.logout.includes("AM") ||
                            updateData.logout.includes("PM")
                              ? formatTimeForInput(updateData.logout)
                              : updateData.logout
                          }
                          onChange={(e) => {
                            console.log(
                              "Log Out input changed:",
                              e.target.value
                            );
                            setUpdateData({
                              ...updateData,
                              logout: formatTimeForDisplay(e.target.value),
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-sm px-3 me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeEditModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white btn-sm px-4"
                      style={{ backgroundColor: "#01AEBE" }}
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteAttendanceModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteAttendanceModalLabel">
                  Delete Attendance
                </h5>
                <p style={{ marginTop: "10px" }}>
                  You're going to delete this attendance record. Are you sure?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                >
                  Yes, Delete It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showEditModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
}

export default Attendance;